import { Flex, Icon, Pill, PillWrapper, Text, ToolIcon } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import type { AgentTopAlert } from "./AgentProfile.constants";
import type { IconProps, PillProps } from "@theorgchartdev/storybook";

const AlertSeverityPillColor: Record<string, NonNullable<PillProps["color"]>> = {
  critical: "error",
  warning: "warning"
};

// Severity chips carry an icon per the mock; unknown severities render a
// plain default pill.
const AlertSeverityIcon: Record<
  string,
  { name: IconProps["name"]; modifier: IconProps["modifier"] }
> = {
  critical: { name: "circle-exclamation", modifier: "error" },
  warning: { name: "triangle-exclamation", modifier: "warning" }
};

const getAffectedLabel = (count: number | null): string | null => {
  if (count == null) {
    return null;
  }
  return count === 1
    ? appstr("1 employee affected")
    : appstr("{n} employees affected", { n: count });
};

type Props = {
  alerts: AgentTopAlert[];
};

export const AgentAlertList = ({ alerts }: Props) => {
  if (alerts.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap="8px" className="ocn-agent-alert-list">
      {alerts.map((alert, index) => {
        const affectedLabel = getAffectedLabel(alert.affectedEmployeeCount);
        return (
          <Flex
            key={`${alert.title}-${index}`}
            direction="row"
            gap="8px"
            alignItems="flex-start"
            className="ocn-agent-alert-row"
            dataTestId={`agent-alert-row-${index}`}
          >
            <Flex direction="column" gap="4px" flex="1">
              <Flex direction="row" gap="8px" alignItems="center">
                {alert.severity && (
                  <PillWrapper>
                    <Pill
                      size="compact"
                      label={alert.severity}
                      color={AlertSeverityPillColor[alert.severity]}
                      leftContent={
                        AlertSeverityIcon[alert.severity] ? (
                          <Icon
                            name={AlertSeverityIcon[alert.severity].name}
                            size="small"
                            modifier={AlertSeverityIcon[alert.severity].modifier}
                          />
                        ) : undefined
                      }
                    />
                  </PillWrapper>
                )}
                {affectedLabel && (
                  <Text size="xsmall" className="oc-font-weight-500">
                    {affectedLabel}
                  </Text>
                )}
              </Flex>
              <Text size="small" className="ocn-agent-alert-row-title">
                {alert.title}
              </Text>
            </Flex>
            {/* TODO Disabled placeholder until the dataset supplies a guardianLink. */}
            <ToolIcon
              name="arrow-up-right-from-square"
              size="xsmall"
              ariaLabel={appstr("Open alert details")}
              dataTestId={`agent-alert-row-${index}-link`}
              disabled={!alert.guardianLink}
              onClick={() =>
                alert.guardianLink &&
                window.open(alert.guardianLink, "_blank", "noopener,noreferrer")
              }
            />
          </Flex>
        );
      })}
    </Flex>
  );
};
